import { useCallback, useMemo, useState } from 'react'
import { defaultBufferFilters } from '../constants'

const cloneDefaults = () => ({
  ...defaultBufferFilters,
  advanced: { ...defaultBufferFilters.advanced },
})

const normaliseDate = (value) => {
  if (!value) return null
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

const filterByTimeWindow = (event, window) => {
  if (window === 'any') {
    return true
  }

  const kickoffDate = normaliseDate(event.kickoff)
  if (!kickoffDate) return true

  const now = new Date()
  const diffMinutes = (kickoffDate.getTime() - now.getTime()) / 1000 / 60

  if (window === 'live') {
    return event.status === 'live' || diffMinutes <= 0
  }
  if (window === '1h') {
    return diffMinutes >= 0 && diffMinutes <= 60
  }
  if (window === '3h') {
    return diffMinutes >= 0 && diffMinutes <= 180
  }
  if (window === 'today') {
    return kickoffDate.toDateString() === now.toDateString()
  }
  if (window === 'tomorrow') {
    const tomorrow = new Date(now)
    tomorrow.setDate(now.getDate() + 1)
    return kickoffDate.toDateString() === tomorrow.toDateString()
  }

  return true
}

const filterByAdvanced = (event, advanced) => {
  const { handicapTrend, oddsDelta, goalLine, updatedWithin } = advanced

  if (handicapTrend && handicapTrend !== 'all') {
    if ((event.handicapTrend ?? 'all') !== handicapTrend) {
      return false
    }
  }

  if (oddsDelta) {
    const numeric = Number(oddsDelta)
    if (!Number.isNaN(numeric)) {
      if (Math.abs(event.oddsDelta ?? 0) < numeric) {
        return false
      }
    }
  }

  if (goalLine && goalLine !== 'any') {
    const goal = Number(event.goalLine ?? 0)
    if (Number.isNaN(goal)) {
      return false
    }
    if (goalLine === 'high' && goal < 3) {
      return false
    }
    if (goalLine === 'balanced' && (goal < 2 || goal > 2.75)) {
      return false
    }
    if (goalLine === 'low' && goal > 1.75) {
      return false
    }
  }

  if (updatedWithin && updatedWithin !== 'any') {
    const updatedAt = normaliseDate(event.lastSyncedAt)
    if (!updatedAt) {
      return false
    }
    const now = new Date()
    const diffMinutes = (now.getTime() - updatedAt.getTime()) / 1000 / 60

    const limitMap = {
      '5m': 5,
      '15m': 15,
      '1h': 60,
      '24h': 24 * 60,
    }
    const threshold = limitMap[updatedWithin]
    if (threshold && diffMinutes > threshold) {
      return false
    }
  }

  return true
}

const sortEvents = (events, sortOption) => {
  const list = [...events]
  const compareByKickoff = (a, b) => {
    const first = normaliseDate(a.kickoff)?.getTime() ?? 0
    const second = normaliseDate(b.kickoff)?.getTime() ?? 0
    return first - second
  }

  switch (sortOption) {
    case 'kickoffAsc':
      return list.sort(compareByKickoff)
    case 'kickoffDesc':
      return list.sort((a, b) => compareByKickoff(b, a))
    case 'leagueAsc':
      return list.sort((a, b) =>
        (a.leagueName ?? '').localeCompare(b.leagueName ?? '', undefined, { sensitivity: 'base' }),
      )
    case 'leagueDesc':
      return list.sort((a, b) =>
        (b.leagueName ?? '').localeCompare(a.leagueName ?? '', undefined, { sensitivity: 'base' }),
      )
    case 'handicap':
      return list.sort(
        (a, b) =>
          Math.abs(b.handicap?.line ?? 0) - Math.abs(a.handicap?.line ?? 0),
      )
    case 'oddsDelta':
      return list.sort((a, b) => Math.abs(b.oddsDelta ?? 0) - Math.abs(a.oddsDelta ?? 0))
    default:
      return list
  }
}

const useBufferFilters = (events = [], initialFilters = defaultBufferFilters) => {
  const [filters, setFilters] = useState(() => ({
    ...cloneDefaults(),
    ...initialFilters,
    advanced: { ...cloneDefaults().advanced, ...(initialFilters?.advanced ?? {}) },
  }))

  const updateFilters = useCallback((next) => {
    setFilters((prev) => ({
      ...prev,
      ...next,
      advanced: { ...prev.advanced },
    }))
  }, [])

  const updateAdvancedFilters = useCallback((nextAdvanced) => {
    setFilters((prev) => ({
      ...prev,
      advanced: {
        ...prev.advanced,
        ...nextAdvanced,
      },
    }))
  }, [])

  const resetFilters = useCallback(() => {
    setFilters(cloneDefaults())
  }, [])

  const filteredEvents = useMemo(() => {
    if (!Array.isArray(events) || !events.length) {
      return []
    }

    const searchTerm = filters.search.trim().toLowerCase()
    const leagueFilter = filters.league
    const timeWindow = filters.timeWindow

    const subset = events.filter((event) => {
      if (leagueFilter && event.leagueId !== leagueFilter) {
        return false
      }

      if (searchTerm) {
        const haystack = [
          event.homeTeam,
          event.awayTeam,
          event.leagueName,
          event.leagueCountry,
          event.id,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()

        if (!haystack.includes(searchTerm)) {
          return false
        }
      }

      if (!filterByTimeWindow(event, timeWindow)) {
        return false
      }

      if (!filterByAdvanced(event, filters.advanced)) {
        return false
      }

      return true
    })

    return sortEvents(subset, filters.sort)
  }, [events, filters])

  const leagueOptions = useMemo(() => {
    const map = new Map()
    events.forEach((event) => {
      if (!event?.leagueId) return
      if (map.has(event.leagueId)) return
      map.set(event.leagueId, {
        value: event.leagueId,
        label: `${event.leagueName ?? event.leagueId}${
          event.leagueCountry ? ` • ${event.leagueCountry}` : ''
        }`,
      })
    })
    return Array.from(map.values()).sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
    )
  }, [events])

  return {
    filters,
    updateFilters,
    updateAdvancedFilters,
    resetFilters,
    filteredEvents,
    derived: {
      leagueOptions,
    },
  }
}

export default useBufferFilters

